import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ChevronDown, Download, Filter, Loader, X } from 'lucide-react';
import { useServices, useToast } from '@/app/providers';
import { useDateFormatter } from '@/shared/hooks';
import { AuditAction, AuditFilter, SystemAuditLog } from '@/shared/types';
import { DataTable, type ColumnDef } from '@/core/ui/components';

const ACTIONS: { value: AuditAction; label: string }[] = [
  { value: AuditAction.CREATE, label: 'Crear' },
  { value: AuditAction.UPDATE, label: 'Actualizar' },
  { value: AuditAction.DELETE, label: 'Eliminar' },
  { value: AuditAction.LOGIN, label: 'Inicio sesión' },
  { value: AuditAction.LOGOUT, label: 'Cierre sesión' },
  { value: AuditAction.EXPORT, label: 'Exportar' },
  { value: AuditAction.ACCESS_GRANTED, label: 'Acceso concedido' },
  { value: AuditAction.ACCESS_DENIED, label: 'Acceso denegado' },
];

const getActionColor = (action: string) => {
  switch (action) {
    case 'CREATE': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    case 'DELETE': return 'text-red-600 bg-red-50 border-red-200';
    case 'UPDATE': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'LOGIN': return 'text-indigo-600 bg-indigo-50 border-indigo-200';
    case 'LOGOUT': return 'text-slate-600 bg-slate-50 border-slate-200';
    case 'ACCESS_GRANTED': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    case 'ACCESS_DENIED': return 'text-red-600 bg-red-50 border-red-200';
    case 'EXPORT': return 'text-purple-600 bg-purple-50 border-purple-200';
    default: return 'text-slate-600 bg-slate-50 border-slate-200';
  }
};

export const AdminAuditList = () => {
  const { auditRepo } = useServices();
  const toast = useToast();
  const { formatDateTime } = useDateFormatter();

  // Filters
  const [filters, setFilters] = useState<AuditFilter>({
    page: 1,
    limit: 10,
  });
  const [showFilters, setShowFilters] = useState(false);

  // Data
  const [logs, setLogs] = useState<SystemAuditLog[]>([]);
  const [meta, setMeta] = useState<{ total: number; page: number; limit: number; totalPages: number; hasNextPage: boolean; hasPreviousPage: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await auditRepo.getPaginatedLogs(filters);
      setLogs(response.data);
      setMeta(response.meta);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar auditoría');
    } finally {
      setLoading(false);
    }
  }, [auditRepo, filters, toast]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Handle filter changes
  const updateFilter = (key: keyof AuditFilter, value: string | number | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: key !== 'page' ? 1 : (value as number), // Reset page when changing filters
    }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: filters.limit });
  };

  const hasActiveFilters = filters.action || filters.resource || filters.dateFrom || filters.dateTo;

  // Export handler
  const handleExport = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      toast.error('Selecciona un rango de fechas para exportar');
      return;
    }

    setExporting(true);
    try {
      const blob = await auditRepo.exportTxt({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        action: filters.action,
        resource: filters.resource,
      });

      // Download file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_${filters.dateFrom}_${filters.dateTo}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Archivo exportado correctamente');
    } catch (e) {
      console.error(e);
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnDef<SystemAuditLog>[] = useMemo(() => [
    {
      id: 'timestamp',
      header: 'Fecha',
      cell: (log) => (
        <span className="text-slate-500 font-mono text-xs whitespace-nowrap">
          {formatDateTime(log.timestamp)}
        </span>
      ),
    },
    {
      id: 'actor',
      header: 'Usuario',
      cell: (log) => (
        <span className="font-medium text-slate-800">{log.actorName}</span>
      ),
    },
    {
      id: 'action',
      header: 'Acción',
      cell: (log) => (
        <span className={`px-2 py-1 rounded text-[10px] font-bold border ${getActionColor(log.action)}`}>
          {log.action}
        </span>
      ),
    },
    {
      id: 'resource',
      header: 'Recurso',
      cell: (log) => (
        <span className="text-slate-600">{log.resource}</span>
      ),
    },
    {
      id: 'details',
      header: 'Detalles',
      cell: (log) => (
        <span className="text-slate-600 max-w-md truncate block" title={log.details}>
          {log.details}
        </span>
      ),
    },
  ], [formatDateTime]);

  const filtersSection = (
    <div className="flex items-center gap-2">
      {/* Filter toggle */}
      <button
        onClick={() => setShowFilters(!showFilters)}
        className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
          hasActiveFilters
            ? 'border-primary-300 bg-primary-50 text-primary-700'
            : 'border-slate-200 text-slate-600 hover:bg-slate-50'
        }`}
      >
        <Filter size={16} />
        Filtros
        {hasActiveFilters && (
          <span className="w-2 h-2 bg-primary-500 rounded-full" />
        )}
        <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
      </button>

      {/* Export button */}
      <button
        onClick={handleExport}
        disabled={exporting || !filters.dateFrom || !filters.dateTo}
        className="flex items-center gap-2 px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        title={!filters.dateFrom || !filters.dateTo ? 'Selecciona fechas para exportar' : 'Exportar a TXT'}
      >
        {exporting ? <Loader size={16} className="animate-spin" /> : <Download size={16} />}
        Exportar
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 animate-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-slate-800">Filtros</h4>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                <X size={12} /> Limpiar filtros
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Action filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Acción</label>
              <select
                value={filters.action || ''}
                onChange={(e) => updateFilter('action', e.target.value as AuditAction)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Todas</option>
                {ACTIONS.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>

            {/* Resource filter */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Recurso</label>
              <input
                type="text"
                value={filters.resource || ''}
                onChange={(e) => updateFilter('resource', e.target.value)}
                placeholder="Ej: User, Payment..."
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
            </div>

            {/* Date from */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Desde</label>
              <div className="relative">
                <input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            {/* Date to */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Hasta</label>
              <div className="relative">
                <input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <DataTable
        data={logs}
        columns={columns}
        keyExtractor={(log) => log.id}
        meta={meta}
        onPageChange={(page) => updateFilter('page', page)}
        pageSize={filters.limit || 10}
        onPageSizeChange={(size) => updateFilter('limit', size)}
        title="Auditoría del Sistema"
        loading={loading}
        emptyMessage="No hay registros de auditoría."
        actions={filtersSection}
      />
    </div>
  );
};
